import { Link, useLocation } from "react-router-dom";
import { Instagram, Linkedin, Mail, Phone } from "lucide-react";
import NewsletterForm from "@/components/NewsletterForm";
import { T } from "@/lib/texts";

export default function Footer() {
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const isFunnel = pathname.startsWith("/checkup");

  // Modalità funnel (traffico Meta Ads): footer minimale, zero distrazioni
  if (isFunnel) {
    return (
      <footer data-testid="site-footer" className="bg-[#2A3229] text-[#F6F3EF] mt-24 relative z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 text-xs text-[#F6F3EF]/50 flex flex-col md:flex-row justify-between gap-2">
          <span>© {new Date().getFullYear()} Roberta Castagna · Tutti i diritti riservati</span>
          <Link to="/privacy" className="hover:text-[#FAC075] transition-colors" data-testid="footer-privacy">Privacy &amp; Cookie Policy</Link>
        </div>
      </footer>
    );
  }

  return (
    <footer data-testid="site-footer" className="bg-[#2A3229] text-[#F6F3EF] mt-24 relative z-10">
      {/* NEWSLETTER STRIP — nascosta in Home (lì c'è già la sezione dedicata) */}
      {!isHome && (
      <div className="border-b border-[#F6F3EF]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-12 md:justify-between">
          <div className="max-w-md">
            <p className="font-display text-2xl tracking-tight leading-snug">Ogni settimana, <span className="text-[#FAC075]">una cosa concreta</span> su cui lavorare.</p>
            <p className="text-sm text-[#F6F3EF]/60 mt-2">Un'azione pratica per migliorare la tua impresa. Niente teoria, niente spam.</p>
          </div>
          <div className="w-full md:w-auto md:min-w-[380px]">
            <NewsletterForm dark testidPrefix="newsletter-footer" />
          </div>
        </div>
      </div>
      )}

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <img
            src="/assets/maze-ocra.png"
            alt="Roberta Castagna" className="h-24 w-auto mb-6"
          />
          <h3 className="font-display text-3xl mb-2 tracking-tight">ROBERTA CASTAGNA</h3>
          <p className="text-xs uppercase tracking-[0.24em] text-[#FAC075] mb-5">Business Consultant</p>
          <p className="text-[#F6F3EF]/70 max-w-md leading-relaxed">
            <T k="footer_claim">Consulenza per le aziende: oltre 10 anni di esperienza nel management per ottimizzare i flussi produttivi e organizzativi, ridurre il carico operativo della direzione e restituire tempo strategico all'imprenditore.</T>
          </p>
        </div>

        <div>
          <p className="eyebrow !text-[#FAC075] mb-4">Navigazione</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/chi-sono" className="hover:text-[#FAC075] transition-colors">Chi sono</Link></li>
            <li><Link to="/percorsi" className="hover:text-[#FAC075] transition-colors">I miei percorsi</Link></li>
            <li><Link to="/metodo" className="hover:text-[#FAC075] transition-colors">Il Metodo</Link></li>
            <li><Link to="/casistudio" className="hover:text-[#FAC075] transition-colors">Casi Studio</Link></li>
            <li><Link to="/contatti" className="hover:text-[#FAC075] transition-colors">Contatti</Link></li>
          </ul>
        </div>

        <div>
          <p className="eyebrow !text-[#FAC075] mb-4">Contatti</p>

          <div className="flex items-center gap-3 text-sm mb-3 text-[#F6F3EF]/90">
            <Mail size={15} className="shrink-0"/>
            <a
              href="mailto:robertacastagna1@gmail.com"
              className="hover:text-[#FAC075] break-all transition-colors"
            >
              robertacastagna1@gmail.com
            </a>
          </div>

          <div className="flex items-center gap-3 text-sm mb-5 text-[#F6F3EF]/90">
            <Phone size={15} className="shrink-0"/>
            <a
              data-testid="footer-phone"
              href="tel:+393475061407"
              className="hover:text-[#FAC075] transition-colors"
            >
              +39 347 506 1407
            </a>
          </div>

          <div className="flex items-center gap-5">
            <a
              data-testid="footer-whatsapp"
              href={"https://wa.me/393475061407?text=" + encodeURIComponent("Ciao Roberta, vorrei maggiori informazioni sui tuoi servizi.")}
              target="_blank"
              rel="noreferrer"
              aria-label="Scrivimi su WhatsApp"
              className="text-[#F6F3EF]/85 hover:text-[#FAC075] hover:-translate-y-0.5 transition-all duration-300 inline-flex items-center"
            >
              <i className="fa-brands fa-whatsapp text-[21px]" aria-hidden="true"/>
            </a>
            <a
              data-testid="footer-in"
              href="https://www.linkedin.com/in/roberta-castagna/"
              target="_blank"
              rel="noreferrer"
              aria-label="LinkedIn"
              className="text-[#F6F3EF]/85 hover:text-[#FAC075] hover:-translate-y-0.5 transition-all duration-300"
            >
              <Linkedin size={20} />
            </a>
            <a
              data-testid="footer-ig"
              href="https://www.instagram.com/robertacastagna_/"
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              className="text-[#F6F3EF]/85 hover:text-[#FAC075] hover:-translate-y-0.5 transition-all duration-300"
            >
              <Instagram size={20} />
            </a>
            <a
              data-testid="footer-gbp"
              href="https://share.google/kOfk1mZ4wFh9qx5TN"
              target="_blank"
              rel="noreferrer"
              aria-label="Roberta Castagna su Google"
              className="text-[#F6F3EF]/85 hover:text-[#FAC075] hover:-translate-y-0.5 transition-all duration-300 inline-flex items-center"
            >
              <i className="fa-brands fa-google text-[19px]" aria-hidden="true"/>
            </a>
          </div>

          <p className="mt-8 text-[11px] tracking-wide text-[#F6F3EF]/50 leading-relaxed">
            Gravina di Catania <span className="mx-1">|</span> P. IVA 06241190872
          </p>
        </div>
      </div>

      <div className="border-t border-[#F6F3EF]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 text-xs text-[#F6F3EF]/50 flex flex-col md:flex-row justify-between gap-2">
          <span>© {new Date().getFullYear()} Roberta Castagna · Tutti i diritti riservati</span>
          <Link to="/privacy" className="hover:text-[#FAC075] transition-colors" data-testid="footer-privacy">Privacy &amp; Cookie Policy</Link>
        </div>
      </div>
    </footer>
  );
}
